import { faFloppyDisk, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import * as React from "react";
import { useState, ReactNode } from "react";
import styled from "styled-components";
import {
  CompanyData,
  ProjectFormData,
  SaveFailReason,
  SaveProjectsResponse,
} from ".";
import { getProjectStatusName, ProjectSummaryFormData } from "./Projects";

const Wrapper = styled.div`
  & .company {
    margin-bottom: 0.2rem;
    font-size: 0.9rem;
    font-weight: bold;
  }
`;

const Table = styled.table`
  max-width: 100%;
  width: 500px;

  & td:nth-child(1) {
    width: 1rem;
  }

  & tr {
    &.status-failed-status-changed td .project-status {
      text-decoration: line-through;
    }

    &.status-failed-not-found {
      text-decoration: line-through;
    }
  }
`;

const Buttons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;

  & button {
    margin-bottom: 0;
  }
`;

const ErrorText = styled.p`
  margin: 0.5rem 0;
`;

const SaveButton = styled.button`
  display: flex;
  gap: 0.2rem;
`;

interface Props {
  projects: ProjectSummaryFormData[];
  company: CompanyData<ProjectFormData>;
  onSaveProjects: (
    changedProjects: ProjectFormData[]
  ) => Promise<SaveProjectsResponse | null>;
  closeModal: () => void;
}

const SaveProjectsModal = ({
  projects,
  company,
  onSaveProjects,
  closeModal,
}: Props) => {
  const [saving, setSaving] = useState(false);
  const [saveResponse, setSaveResponse] = useState(
    null as SaveProjectsResponse | null
  );
  const [saveErrorInfo, _setSaveErrorInfo] = useState(
    new Map<SaveFailReason, [string, string]>([
      [
        SaveFailReason.NotFound,
        ["#e61919", "Projektet är borttaget av en annan användare"],
      ],
      [
        SaveFailReason.StatusChanged,
        ["#dfd118", "Statusen är ändrad av en annan användare"],
      ],
    ])
  );

  const onClickSave = async () => {
    setSaving(true);
    const response = await onSaveProjects(projects);
    setSaveResponse(response);
    setSaving(false);

    if (response?.status === 200) {
      setSaveResponse(null);
      closeModal();
    }
  };

  const renderProjectSaveStatus = (projectId: number): ReactNode => {
    if (saveResponse == null) {
      return null;
    }

    const failReason = saveResponse.failed?.find(
      (p) => p.id === projectId
    )?.reason;

    if (failReason != null) {
      const errorValues = saveErrorInfo.get(failReason)!;
      return (
        <FontAwesomeIcon
          icon={faFloppyDisk}
          color={errorValues[0]}
          title={errorValues[1]}
        />
      );
    }

    return null;
  };

  const getProjectClassNames = (projectId: number): string[] => {
    const result = [] as string[];

    if (saveResponse?.successful?.indexOf(projectId) !== -1) {
      result.push("status-success");
    } else {
      const failReason = saveResponse.failed?.find(
        (p) => p.id === projectId
      )?.reason;

      switch (failReason) {
        case SaveFailReason.StatusChanged: {
          result.push("status-failed-status-changed");
          break;
        }
        case SaveFailReason.NotFound: {
          result.push("status-failed-not-found");
          break;
        }
      }
    }

    return result;
  };

  return (
    <Wrapper>
      <p className="company">{company.name}</p>
      <Table className="projects display table-small">
        <thead>
          <tr>
            <td>
              <FontAwesomeIcon
                fontStyle="regular"
                icon={faFloppyDisk}
                title="Sparresultat"
              />
            </td>
            <td>Projekt</td>
            <td>Ny status</td>
          </tr>
        </thead>
        <tbody>
          {projects.map((project) => (
            <tr
              key={project.id}
              className={classNames(getProjectClassNames(project.id))}
            >
              <td>{renderProjectSaveStatus(project.id)}</td>
              <td>{project.name}</td>
              <td>
                <span className="project-status">
                  {getProjectStatusName(project.newStatus)}
                </span>
                {saveResponse?.failed?.find(
                  (p) =>
                    p.id === project.id &&
                    p.reason === SaveFailReason.StatusChanged
                ) && (
                  <span>
                    &nbsp;{getProjectStatusName(project.serverStatus!)}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {saveResponse?.message?.length && (
        <div>
          <div>
            <ErrorDescription
              error={SaveFailReason.NotFound}
              saveErrorInfo={saveErrorInfo}
            />
            <ErrorDescription
              error={SaveFailReason.StatusChanged}
              saveErrorInfo={saveErrorInfo}
            />
          </div>
          <ErrorText className="form-error is-visible">
            {saveResponse?.message}
          </ErrorText>
        </div>
      )}
      <Buttons>
        <SaveButton
          className="button primary"
          onClick={onClickSave}
          disabled={saving}
        >
          Spara
          {saving && <FontAwesomeIcon icon={faSpinner} spin={true} />}
        </SaveButton>
        <button className="button secondary" onClick={closeModal}>
          Avbryt
        </button>
      </Buttons>
    </Wrapper>
  );
};

interface ErrorDescriptionProps {
  error: SaveFailReason;
  saveErrorInfo: Map<SaveFailReason, [string, string]>;
}

const ErrorDescriptionWrapper = styled.div`
  font-size: 0.8rem;
`;

const ErrorDescription = ({ error, saveErrorInfo }: ErrorDescriptionProps) => {
  return (
    <ErrorDescriptionWrapper>
      <FontAwesomeIcon
        icon={faFloppyDisk}
        color={saveErrorInfo.get(error)?.[0]}
      />{" "}
      = {saveErrorInfo.get(error)?.[1]}
    </ErrorDescriptionWrapper>
  );
};

export default SaveProjectsModal;
